class SickToesColorSwatches extends HTMLElement {
  connectedCallback() {
    this.selectedValueEl = this.querySelector('[data-sicktoes-selected-value]');
    this.refreshButton = this.querySelector('[data-sicktoes-refresh]');
    this.optionName = (this.dataset.colorOptionName || 'Color').trim();

    this.onDocumentChange = this.handleDocumentChange.bind(this);
    this.onVariantChange = this.handleVariantChange.bind(this);

    this.bindEvents();

    setTimeout(() => {
      this.syncFromNativePicker();
      this.updateVisibleSizeOptions();
    }, 80);

    document.addEventListener('change', this.onDocumentChange);
    document.addEventListener('variant:change', this.onVariantChange);

    if (window.Shopify && Shopify.designMode && this.refreshButton) {
      this.refreshButton.hidden = false;
      this.refreshButton.addEventListener('click', () => {
        this.reinitialize();
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onDocumentChange);
    document.removeEventListener('variant:change', this.onVariantChange);
  }

  handleDocumentChange(event) {
    const target = event.target;
    if (!target) return;

    if (
      target.matches('select[name^="options["]') ||
      target.matches('input[type="radio"]') ||
      target.matches('input[name="id"]')
    ) {
      setTimeout(() => {
        this.syncFromNativePicker();
        this.updateVisibleSizeOptions();
      }, 80);
    }
  }

  handleVariantChange() {
    setTimeout(() => {
      this.syncFromNativePicker();
      this.updateVisibleSizeOptions();
    }, 80);
  }

  bindEvents() {
    const radios = this.querySelectorAll('.sicktoes-swatch-input');

    radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        const value = radio.dataset.optionValue;
        this.setSelectedText(value);
        this.selectNativeOption(value);
      });
    });
  }

  setSelectedText(value) {
    if (this.selectedValueEl) {
      this.selectedValueEl.textContent = value;
    }
  }

  getProductRoot() {
    return (
      this.closest('product-info') ||
      this.closest('.product') ||
      this.closest('section') ||
      document
    );
  }

  getVariantSelects() {
    return this.getProductRoot().querySelector('variant-selects');
  }

  getFieldsetByOptionName(optionName) {
    const variantSelects = this.getVariantSelects();
    if (!variantSelects) return null;

    const optionNameLower = optionName.toLowerCase();

    const fieldsets = Array.from(
      variantSelects.querySelectorAll('fieldset[data-option-name], fieldset.product-form__input')
    );

    return (
      fieldsets.find((fieldset) => {
        const dataName = (fieldset.getAttribute('data-option-name') || '').trim().toLowerCase();
        const legend = fieldset.querySelector('legend');
        const legendText = legend ? legend.textContent.trim().toLowerCase() : '';
        return dataName === optionNameLower || legendText.startsWith(optionNameLower);
      }) || null
    );
  }

  getSelectByOptionName(optionName) {
    const variantSelects = this.getVariantSelects();
    if (!variantSelects) return null;

    const selects = Array.from(variantSelects.querySelectorAll('select[name^="options["]'));

    return (
      selects.find((select) => {
        const name = select.name.replace('options[', '').replace(']', '').trim().toLowerCase();
        return name === optionName.toLowerCase();
      }) || null
    );
  }

  getNativeColorFieldset() {
    return this.getFieldsetByOptionName(this.optionName);
  }

  getNativeColorSelect() {
    return this.getSelectByOptionName(this.optionName);
  }

  selectNativeOption(optionValue) {
    const variantSelects = this.getVariantSelects();
    if (!variantSelects) return;

    let didUpdate = false;

    const nativeFieldset = this.getNativeColorFieldset();
    if (nativeFieldset) {
      const nativeRadios = Array.from(nativeFieldset.querySelectorAll('input[type="radio"]'));
      const match = nativeRadios.find((radio) => radio.value === optionValue);

      if (match) {
        match.checked = true;
        match.dispatchEvent(new Event('input', { bubbles: true }));
        match.dispatchEvent(new Event('change', { bubbles: true }));
        didUpdate = true;
      }
    }

    if (!didUpdate) {
      const nativeSelect = this.getNativeColorSelect();
      if (nativeSelect) {
        nativeSelect.value = optionValue;
        nativeSelect.dispatchEvent(new Event('input', { bubbles: true }));
        nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        didUpdate = true;
      }
    }

    if (!didUpdate) {
      console.warn('[Sick Toes Swatches] Could not find native color option for:', optionValue);
      return;
    }

    setTimeout(() => {
      this.syncFromNativePicker();
      this.updateVisibleSizeOptions();

      const hiddenVariantIdInputs = this.getProductRoot().querySelectorAll('input[name="id"]');
      hiddenVariantIdInputs.forEach((input) => {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }, 100);
  }

  syncFromNativePicker() {
    let activeValue = null;

    const nativeFieldset = this.getNativeColorFieldset();
    if (nativeFieldset) {
      const checked = nativeFieldset.querySelector('input[type="radio"]:checked');
      if (checked) activeValue = checked.value;
    }

    if (!activeValue) {
      const nativeSelect = this.getNativeColorSelect();
      if (nativeSelect) activeValue = nativeSelect.value;
    }

    if (!activeValue) return;

    const customRadios = this.querySelectorAll('.sicktoes-swatch-input');
    customRadios.forEach((radio) => {
      radio.checked = radio.value === activeValue;
    });

    this.setSelectedText(activeValue);
  }

  isUnavailableRadio(radio) {
    return (
      radio.disabled ||
      radio.classList.contains('disabled') ||
      radio.hasAttribute('disabled') ||
      radio.getAttribute('aria-disabled') === 'true'
    );
  }

  updateVisibleSizeOptions() {
    const sizeFieldset =
      this.getFieldsetByOptionName('Size') ||
      this.getFieldsetByOptionName('Talla');

    if (!sizeFieldset) return;

    const radios = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
    if (!radios.length) return;

    radios.forEach((radio) => {
      const label = sizeFieldset.querySelector(`label[for="${radio.id}"]`);
      if (!label) return;

      const unavailable = this.isUnavailableRadio(radio);

      radio.style.display = unavailable ? 'none' : '';
      label.style.display = unavailable ? 'none' : '';
    });

    const checkedVisible = radios.find((radio) => radio.checked && !this.isUnavailableRadio(radio));

    if (!checkedVisible) {
      const firstAvailable = radios.find((radio) => !this.isUnavailableRadio(radio));

      if (firstAvailable) {
        firstAvailable.checked = true;
        firstAvailable.dispatchEvent(new Event('input', { bubbles: true }));
        firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  reinitialize() {
    this.syncFromNativePicker();
    this.updateVisibleSizeOptions();
  }
}

if (!customElements.get('sicktoes-color-swatches')) {
  customElements.define('sicktoes-color-swatches', SickToesColorSwatches);
}